import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { ACCESS_DENIED } from "../lib/auth";
import { useGrpcClient } from "../lib/grpc";
import {
  BookAndJoinType,
  GetLoyaltyBrandsManagedByPropertyRequest,
  RegistrationTextType,
} from "../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { useKeycloakAuthz } from "./keycloak-provider";
import { LoyaltyBrandAPIPromiseClient } from "../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_grpc_web_pb";
import { useProperty } from "./property-provider";

export interface IPropertyLoyaltyBrand {
  managedByList: string[];
  relatedDomainsList: string[];
  ticker: string;
  id: string;
  externalSystemId: string;
  transactionExpirationLimit: number;
  usedByList: string[];
  mainDomain?: string;
  authorizationServiceDomain?: string;
  bookAndJoinType: BookAndJoinType;
  registrationTextType: RegistrationTextType;
}

export interface LoyaltyBrandProviderProps {
  propertyTicker: string;
  children: ReactNode | ((value: LoyaltyBrandValue) => ReactNode);
}

type LoyaltyBrandValue = IPropertyLoyaltyBrand | undefined | null;
const LoyaltyBrand = createContext<LoyaltyBrandValue>(undefined);

export function LoyaltyBrandProvider({ propertyTicker, children }: LoyaltyBrandProviderProps) {
  const [data, setData] = useState<LoyaltyBrandValue>(undefined);
  const keycloakAuthz = useKeycloakAuthz();
  const grpcClient = useGrpcClient(LoyaltyBrandAPIPromiseClient);
  const property = useProperty();

  useEffect(() => {
    async function load() {
      try {
        const token = property.token;
        const request = new GetLoyaltyBrandsManagedByPropertyRequest();
        request.setPropertyTicker(propertyTicker);
        const response = await grpcClient.getLoyaltyBrandsManagedByProperty(request, {
          Authorization: `Bearer ${token}`,
        });
        const brandsList = response
          .toObject()
          .brandsList.map(
            ({
              managedByList,
              relatedDomainsList,
              ticker,
              transactionExpirationLimit,
              usedByList,
              id,
              externalSystemId,
              mainDomain,
              authorizationServiceDomain,
              bookAndJoinType,
              registrationTextType,
            }) => ({
              managedByList,
              relatedDomainsList,
              ticker,
              transactionExpirationLimit,
              usedByList,
              id,
              externalSystemId,
              mainDomain,
              authorizationServiceDomain,
              bookAndJoinType,
              registrationTextType,
            })
          );
        setData(brandsList[0]);
      } catch (e) {
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
        } else {
          console.error("Error retrieving rpt", e);
        }
      }
    }

    load();
  }, [grpcClient, keycloakAuthz, property.token, propertyTicker]);

  return (
    <LoyaltyBrand.Provider value={data}>
      {typeof children === "function" ? children(data) : children}
    </LoyaltyBrand.Provider>
  );
}

export function useLoyaltyBrand(): LoyaltyBrandValue {
  return useContext(LoyaltyBrand);
}
